import React, { useState } from 'react';

import {
  CancelSurveyModal,
  CancelSurveyFeedback,
  useSciDomain,
} from 'components';
import { CountdownTimer } from 'components/CountdownTimer';
import { EcomCardForm } from 'components/EcomCardForm';
import { EcomOtpForm } from 'components/EcomOtpForm';
import { EcomRedirect } from 'components/EcomRedirect';
import { ProviderPaymentRedirect } from 'components/ProviderPaymentRedirect';
import { CustomerConfirmStatusDetails, OrderStatus, SciTemplate } from 'enums';
import { useCancelSurveyReasons, useOrderContext } from 'hooks';
import {
  OrderLayoutProps,
  PaymentCardViewProps,
  PaymentDetailsViewProps,
  PaymentLoadingViewProps,
  PaymentRulesViewProps,
  PaymentStatusViewProps,
} from 'types';
import {
  DefaultCustomerPaidReceipts,
  DefaultLayout,
  DefaultPaymentCard,
  DefaultPaymentDetails,
  DefaultPaymentLoading,
  DefaultPaymentRulesView,
  DefaultPaymentStatus,
} from 'views/DefaultOrderView/components';
import { SqAlternativeOrderAmountSelectionModal } from 'views/SqOrderView';

type Props = {
  overrideComponents?: {
    Layout?: React.FC<OrderLayoutProps>;
    PaymentStatus?: React.FC<PaymentStatusViewProps>;
    PaymentDetails?: React.FC<PaymentDetailsViewProps>;
    PaymentRules?: React.FC<PaymentRulesViewProps>;
    PaymentLoading?: React.FC<PaymentLoadingViewProps>;
    CustomerPaidReceipts?: React.FC;
    PaymentCard?: React.FC<PaymentCardViewProps>;
  };
};

export const BaseOrderView: React.FC<Props> = ({
  overrideComponents: {
    Layout = DefaultLayout,
    PaymentStatus = DefaultPaymentStatus,
    PaymentDetails = DefaultPaymentDetails,
    PaymentRules = DefaultPaymentRulesView,
    PaymentLoading = DefaultPaymentLoading,
    CustomerPaidReceipts = DefaultCustomerPaidReceipts,
    PaymentCard = DefaultPaymentCard,
  } = {},
}) => {
  const { data: sciDomainConfig } = useSciDomain();
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);

  const {
    order,
    refetchOrderStatus,
    tradeMethodSelection: {
      formattedTradeMethods,
      selectedTradeMethod,
      startPaymentSyncFailed,
    },
    paymentForm: {
      customerFields,
      isCustomerDataConfirmed,
      customerDataBeforePayment,
      paymentFieldsLoading,
    },
    receiptsResult: { canManageReceipts },
    orderActions: {
      actions,
      selectPaymentType: onSelectPaymentType,
      submitEcomCard,
      submitEcomConfirmCode,
      isEcomSubmitInProgress,
      isCancelLoading,
      cancelWithFeedback,
    },
    paymentTypeFlags: { isEcom },
    requisitesPaymentLink,
    providerPaymentUrl,
    paymentFormValues,
    status,
    isTSBPSingleBankSelection,
    isStartPaymentSyncInProgress,
    isStartRequisitesCascadeLoading,
    formattedPaymentFields,
    shouldShowQrCode,
    onPaymentFormValuesChange,
    currencySymbol,
    showAlternativeSumModal,
    onSelectAlternativeAmount,
    onSelectAlternativeTradeMethod,
    onDismissAlternativeSumModal,
  } = useOrderContext();

  const isSqTemplate = sciDomainConfig?.template === SciTemplate.SQ;
  const { reasons: cancelSurveyReasons } = useCancelSurveyReasons();

  const effectiveCancelAction = isSqTemplate
    ? actions.cancel
    : { ...actions.cancel, onClick: () => setIsSurveyOpen(true) };

  const handleSurveyConfirm = (feedback: CancelSurveyFeedback) => {
    setIsSurveyOpen(false);
    const hasFeedback =
      feedback.reasons.length > 0 || feedback.comment.trim().length > 0;
    cancelWithFeedback(hasFeedback ? feedback : undefined);
  };

  const handleSurveyDismiss = () => setIsSurveyOpen(false);

  const canShowPaymentDetails =
    !!selectedTradeMethod ||
    formattedPaymentFields.length > 0 ||
    shouldShowQrCode;

  const shouldShowPaymentCard =
    !!selectedTradeMethod &&
    !isCustomerDataConfirmed &&
    (customerDataBeforePayment ||
      order?.statusDetails === CustomerConfirmStatusDetails.CustomerPayed);

  // The 3DS/gateway link comes from the provider's payment_url; only follow it when it is a plain http(s)
  // URL so a malicious/misconfigured provider cannot inject a javascript:/data: (or other scheme) href
  // XSS into the SCI. http is permitted (local mocks, non-TLS providers); only script schemes are blocked.
  const safePaymentLink =
    requisitesPaymentLink && /^https?:\/\//i.test(requisitesPaymentLink)
      ? requisitesPaymentLink
      : null;

  // Redirect-type ecom providers (e.g. manypay) map their active state to CustomerConfirm and carry the
  // gateway link in payment_url; send the customer there instead of the generic P2P requisites view.
  // WaitingConfirmation is the fastpay wait-for-code step and is handled by the OTP form below, never a
  // redirect — even though that order also carries a payment_url (the 3DS page).
  const isEcomGatewayRedirect =
    isEcom && !!safePaymentLink && status === OrderStatus.CustomerConfirm;

  // P2P provider with useProviderPaymentUrl enabled (shop- or provider-level): the backend sends
  // providerPaymentUrl only when the redirect should happen, so presence is the whole signal. Same
  // scheme guard as safePaymentLink — never follow non-http(s) links.
  const safeProviderPaymentUrl =
    providerPaymentUrl && /^https?:\/\//i.test(providerPaymentUrl)
      ? providerPaymentUrl
      : null;

  const isProviderGatewayRedirect =
    !isEcomGatewayRedirect &&
    !!safeProviderPaymentUrl &&
    status === OrderStatus.CustomerConfirm;

  // Keep the ecom card form mounted while the payment sync is in flight (and out of the PaymentLoading
  // swap below). Unmounting it on submit dropped the customer's typed card, so a rejected/failed sync
  // came back to a blank form.
  const isEcomCardEntry =
    status === OrderStatus.New && isEcom && !startPaymentSyncFailed;

  // Countdown for the ecom entry/OTP screens; the order carries statusTimeoutAt for New (30m) and
  // WaitingConfirmation/CustomerConfirm (payment timeout), matching the platform-side timeout crons.
  const ecomTimer = order?.statusTimeoutAt ? (
    <div className="tw-mb-4 tw-flex tw-justify-center">
      <CountdownTimer order={order} refetchOrderStatus={refetchOrderStatus} />
    </div>
  ) : null;

  return (
    <Layout>
      {status === OrderStatus.New &&
        !isStartRequisitesCascadeLoading &&
        (startPaymentSyncFailed ? (
          <PaymentStatus actions={[actions.selectTradeMethod]} />
        ) : isEcom ? (
          <>
            {ecomTimer}
            <EcomCardForm
              onSubmit={submitEcomCard}
              onCancel={() => effectiveCancelAction.onClick?.()}
              isLoading={isEcomSubmitInProgress || isStartPaymentSyncInProgress}
            />
          </>
        ) : (
          !isStartPaymentSyncInProgress && (
            <PaymentRules
              tradeMethods={formattedTradeMethods}
              selectedTradeMethod={selectedTradeMethod}
              handleSelect={onSelectPaymentType}
            />
          )
        ))}
      {isEcomGatewayRedirect && safePaymentLink && (
        <EcomRedirect paymentLink={safePaymentLink} />
      )}
      {isProviderGatewayRedirect && safeProviderPaymentUrl && (
        <ProviderPaymentRedirect paymentLink={safeProviderPaymentUrl} />
      )}
      {isEcom && status === OrderStatus.WaitingConfirmation && (
        <>
          {ecomTimer}
          <EcomOtpForm
            onSubmit={submitEcomConfirmCode}
            onCancel={() => effectiveCancelAction.onClick?.()}
            isLoading={isEcomSubmitInProgress}
          />
        </>
      )}
      {(status === OrderStatus.Requisites ||
        paymentFieldsLoading ||
        (isStartPaymentSyncInProgress && !isEcomCardEntry) ||
        isStartRequisitesCascadeLoading) && (
        <>
          <PaymentLoading status={OrderStatus.Requisites} />
          <SqAlternativeOrderAmountSelectionModal
            open={showAlternativeSumModal}
            orderId={order?.id ?? ''}
            currency={currencySymbol}
            alternativeAmountEnabled={order?.shop?.alternativeAmountEnabled}
            alternativeTradeMethodEnabled={
              order?.shop?.alternativeTradeMethodEnabled
            }
            onSelect={onSelectAlternativeAmount}
            onSelectTradeMethod={onSelectAlternativeTradeMethod}
            onDismiss={onDismissAlternativeSumModal}
          />
        </>
      )}
      {status === OrderStatus.CustomerConfirm &&
        !isEcomGatewayRedirect &&
        !isProviderGatewayRedirect &&
        !paymentFieldsLoading &&
        canShowPaymentDetails &&
        (shouldShowPaymentCard ? (
          <PaymentCard
            tradeMethod={selectedTradeMethod}
            customerFields={customerFields}
            paymentFormValues={paymentFormValues}
            onPaymentFormValuesChange={onPaymentFormValuesChange}
            refetchOrderStatus={refetchOrderStatus}
            actions={
              isTSBPSingleBankSelection
                ? [effectiveCancelAction]
                : [effectiveCancelAction, actions.confirmCard]
            }
          />
        ) : order?.statusDetails !==
          CustomerConfirmStatusDetails.CustomerPayed ? (
          <PaymentDetails
            actions={[effectiveCancelAction, actions.confirmPayment]}
          />
        ) : (
          order?.shop?.collectCustomerReceipts && <CustomerPaidReceipts />
        ))}
      {[OrderStatus.TraderConfirm, OrderStatus.HoldCompleted].includes(
        status,
      ) && <PaymentLoading status={status} />}
      {(status === OrderStatus.Completed ||
        status === OrderStatus.Cancelled ||
        status === OrderStatus.Dispute) && (
        <PaymentStatus
          actions={[actions.backToShop]}
          canManageReceipts={canManageReceipts}
        />
      )}

      {!isSqTemplate && (
        <CancelSurveyModal
          open={isSurveyOpen}
          reasons={cancelSurveyReasons}
          onConfirm={handleSurveyConfirm}
          onDismiss={handleSurveyDismiss}
          isLoading={isCancelLoading}
        />
      )}
    </Layout>
  );
};
